import classNames from 'classnames';
import React, { forwardRef, HTMLAttributeAnchorTarget } from 'react';
import Icon from '@/components/atoms/icon/icon';
import Typography from '@/components/atoms/typography/typography';
import Link, { LinkProps } from '@/components/utilities/link/link';
import styles from './anchor-link.module.scss';

export interface AnchorLinkProps {
  /** The class name to apply */
  className?: string;
  /** Indicates whether the button is disabled */
  disabled?: boolean;
  /** Indicates whether the button is focused */
  focused?: boolean;
  /** Indicates whether the button is hovered */
  hovered?: boolean;
  /** The icon to show */
  icon?: IconName;
  /** Indicates whether this is used in a highlight-section */
  isHighlightSection?: boolean;
  /** An event that is triggered on click */
  onClick?: LinkProps['onClick'];
  /** The tab index of the component */
  tabIndex?: number;
  /** The target type of the link */
  target?: HTMLAttributeAnchorTarget;
  /** The text to show */
  text?: string;
  /** The type of button to use */
  type?: React.DetailedHTMLProps<
    React.ButtonHTMLAttributes<HTMLAnchorElement | HTMLButtonElement>,
    HTMLAnchorElement | HTMLButtonElement
  >['type'];
  /** The url to link to */
  url?: string;
}

const AnchorLink = forwardRef<
  HTMLAnchorElement | HTMLButtonElement,
  AnchorLinkProps
>(
  (
    {
      className,
      disabled = false,
      focused = false,
      hovered = false,
      icon,
      isHighlightSection = false,
      onClick,
      text,
      url,
      ...rest
    },
    ref,
  ) => {
    return (
      <Link
        {...rest}
        ref={ref}
        className={classNames(
          styles.component,
          {
            [styles.isDisabled]: disabled ?? !url,
            [styles.isFocused]: focused,
            [styles.isHighlightSection]: isHighlightSection,
            [styles.isHovered]: hovered,
          },
          className,
        )}
        onClick={onClick}
        url={url}
      >
        {icon && <Icon className={styles.icon} name={icon} size={12} />}

        {text && <Typography tagStyle="bodyMedium">{text}</Typography>}
      </Link>
    );
  },
);

AnchorLink.displayName = 'AnchorLink';

export default AnchorLink;
